import styled from "@emotion/styled";
import { Box, Chip, Tooltip } from "@mui/material";
import { ExchangeIcon } from "src/Common/components/ExchangeIcon";
import { MarkAsReadCrypto } from "src/Modules/Arbitrage/types";
import { CryptoIcon } from "src/Common";
import { useMutation, useQueryClient } from "react-query";
import { useMarkAsReadCrypto } from "src/Modules/Arbitrage/context";
import { toast } from "react-toastify";
import { DateTime, Interval } from "luxon";
import humanizeDuration from "humanize-duration";
import { deleteCryptoMarkedAsRead } from "src/Api";
import { CRYPTOS_READ, ORDER_BOOKS, TICKERS } from "src/Api/endpoints";
import {
  DataGrid,
  GridActionsCellItem,
  GridColumns,
  GridToolbar,
} from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import { useCallback, useMemo } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

interface Props {
  data: MarkAsReadCrypto[];
  isLoading: boolean;
}

export const MarkedAsReadTable = ({ data, isLoading }: Props) => {
  const queryClient = useQueryClient();
  const { open: openMarkAsReadDetail } = useMarkAsReadCrypto();

  const { mutateAsync: deleteAsync, isLoading: isDeleteLoading } = useMutation(
    (id: string) => {
      return deleteCryptoMarkedAsRead(id);
    }
  );

  const handleDelete = useCallback(
    async (markedAsRead: MarkAsReadCrypto) => {
      deleteAsync(markedAsRead.id)
        .then(() =>
          toast(`${markedAsRead.name} successfully deleted!`, {
            theme: "dark",
            type: "success",
            autoClose: 8000,
            pauseOnFocusLoss: false,
          })
        )
        .finally(() => {
          queryClient.invalidateQueries(CRYPTOS_READ);
          queryClient.invalidateQueries([TICKERS, ORDER_BOOKS]);
        });
    },
    [deleteAsync, queryClient]
  );

  const handleCopyAddress = (val: string) => {
    navigator.clipboard.writeText(val).then(() => {
      toast("Address copied to clipboard", {
        theme: "dark",
        type: "success",
        autoClose: 8000,
        pauseOnFocusLoss: false,
      });
    });
  };

  const handleEdit = useCallback(
    (markedAsRead: MarkAsReadCrypto) =>
      openMarkAsReadDetail({
        id: markedAsRead.id,
        name: markedAsRead.name,
        quoteCoin: markedAsRead.quoteCoin,
        exchange: markedAsRead.exchange,
        reason: markedAsRead.reason,
        endDate: markedAsRead.endDate,
      }),
    [openMarkAsReadDetail]
  );

  const columns = useMemo<GridColumns>(
    () => [
      {
        headerName: "Crypto",
        field: "crypto",
        type: "string",
        flex: 1,
        minWidth: 100,
        valueGetter: (params) => params.value.symbol,
        renderCell: ({ row: { crypto } }) => (
          <Box display="flex" alignItems="center">
            <CryptoIcon
              slug={crypto.slug}
              style={{ height: "1rem", paddingRight: "0.2rem" }}
            />
            <Label>{crypto.symbol}</Label>
          </Box>
        ),
      },
      {
        headerName: "Quote Coin",
        field: "quoteCoin",
        type: "string",
        flex: 1,
        minWidth: 100,
        renderCell: ({ value }) =>
          value ? (
            <Box display="flex" alignItems="center">
              <CryptoIcon
                symbol={value}
                style={{ height: "1rem", paddingRight: "0.2rem" }}
              />
              <Label>{value}</Label>
            </Box>
          ) : (
            <Chip
              variant="outlined"
              style={{
                fontSize: "0.5rem",
                margin: 0,
              }}
              size="small"
              label="None"
            />
          ),
      },
      {
        headerName: "Exchange",
        field: "exchange",
        type: "string",
        flex: 1,
        minWidth: 120,
        renderCell: ({ value }) => (
          <Box display="flex" alignItems="center">
            <ExchangeIcon name={value} style={{ height: "1.2rem" }} />
            <Label>{value}</Label>
          </Box>
        ),
      },
      {
        headerName: "Reason",
        field: "reason",
        type: "string",
        align: "center",
        flex: 1,
        minWidth: 150,
        renderCell: ({ value }) => (
          <Chip
            style={{
              fontSize: "0.5rem",
              margin: 0,
            }}
            size="small"
            label={value}
          />
        ),
      },
      {
        headerName: "End Date",
        field: "endDate",
        type: "string",
        flex: 1,
        minWidth: 150,
        renderCell: ({ value }) => {
          const dt = DateTime.fromJSDate(new Date(value)); //TODO: use From ISo
          const duration = Interval.fromDateTimes(DateTime.now(), dt)
            .toDuration()
            .valueOf();
          return (
            <Tooltip
              arrow
              title={`( ${humanizeDuration(duration, {
                language: "en",
                units: ["y", "mo", "d", "h", "m"],
                maxDecimalPoints: 1,
              })} )`}
            >
              <div>
                <div>
                  {dt.toLocaleString({
                    weekday: "short",
                    month: "short",
                    day: "2-digit",
                    year: "numeric",
                  })}
                </div>
                <div>{dt.toLocaleString(DateTime.TIME_SIMPLE)}</div>
              </div>
            </Tooltip>
          );
        },
      },
      {
        headerName: "Actions",
        field: "actions",
        type: "actions",
        flex: 0.5,
        minWidth: 150,
        getActions: ({ row }) => {
          const address = row?.crypto?.platform?.token_address;

          return [
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit"
              onClick={() => handleEdit(row)}
              showInMenu={false}
              placeholder="Edit"
              onPointerEnterCapture={() => {}}
              onPointerLeaveCapture={() => {}}
              onResize={() => {}}
              onResizeCapture={() => {}}
            />,
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => handleDelete(row)}
              showInMenu={false}
              placeholder="Delete"
              onPointerEnterCapture={() => {}}
              onPointerLeaveCapture={() => {}}
              onResize={() => {}}
              onResizeCapture={() => {}}
            />,
            <GridActionsCellItem
              disabled={!address}
              showInMenu={false}
              placeholder="Copy Address"
              onPointerEnterCapture={() => {}}
              onPointerLeaveCapture={() => {}}
              onResize={() => {}}
              onResizeCapture={() => {}}
              icon={
                <Tooltip title="Copy address">
                  <ContentCopyIcon />
                </Tooltip>
              }
              label="Copy Address"
              onClick={() => !!address && handleCopyAddress(address)}
            />,
          ];
        },
      },
    ],
    [handleDelete, handleEdit]
  );

  return (
    <Box height="85vh">
      <DataGrid
        loading={isLoading || isDeleteLoading}
        style={{ border: "none" }}
        headerHeight={36}
        density="standard"
        getRowId={(r) => r.id}
        rows={data}
        columns={columns}
        rowsPerPageOptions={[50, 100]}
        components={{ Toolbar: GridToolbar }}
        componentsProps={{
          toolbar: {
            showQuickFilter: true,
          },
        }}
      />
    </Box>
  );
};

const Label = styled.span({
  fontWeight: 700,
  paddingLeft: "0.5rem",
  fontSize: "0.7rem",
});
